import { clsx, type ClassValue } from "clsx";
import { twMerge } from "tailwind-merge";
import commonEmailDomains from "@/lib/common-email-domains.json";
import { EnvConfigurationError } from "@/lib/errors";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

/**
 * Set of common public email domains (gmail, yahoo, hotmail, etc.)
 * Loaded from common-email-domains.json
 */
const COMMON_EMAIL_DOMAINS: Set<string> = new Set(commonEmailDomains);

/**
 * Check if an email address uses a common public email provider
 * @param email - The email address to check
 * @returns true if the email uses a common public provider, false otherwise
 */
export function isCommonEmailDomain(email: string): boolean {
  const domain = email.split("@")[1]?.toLowerCase();
  if (!domain) return false;
  return COMMON_EMAIL_DOMAINS.has(domain);
}

/**
 * Check if an email address uses a corporate/business email domain
 * @param email - The email address to check
 * @returns true if the email uses a corporate domain, false otherwise
 */
export function isCorporateEmail(email: string): boolean {
  return !isCommonEmailDomain(email);
}

export async function fetchLogoFromWebsite(website: string): Promise<string> {
  try {
    const response = await fetch(website);
    const html = await response.text();

    // Try to find logo in various places: favicon (prioritized), og:image, or first img tag
    let logoUrl: string | null = null;

    // Check for favicon/icon link tag (prioritized)
    // Try apple-touch-icon first (usually higher quality), then regular icon, then shortcut icon
    const appleTouchIconMatch = html.match(/<link[^>]+rel=["']apple-touch-icon["'][^>]+href=["']([^"']+)["']/i);
    if (appleTouchIconMatch) {
      logoUrl = appleTouchIconMatch[1];
    }

    if (!logoUrl) {
      const iconMatch = html.match(/<link[^>]+rel=["'](?:icon|shortcut icon)["'][^>]+href=["']([^"']+)["']/i);
      if (iconMatch) {
        logoUrl = iconMatch[1];
      }
    }

    // Fallback to og:image meta tag
    if (!logoUrl) {
      const ogImageMatch = html.match(/<meta[^>]+property=["']og:image["'][^>]+content=["']([^"']+)["']/i);
      if (ogImageMatch) {
        logoUrl = ogImageMatch[1];
      }
    }

    // Last fallback to first img tag
    if (!logoUrl) {
      const imgMatch = html.match(/<img[^>]+src=["']([^"']+)["']/i);
      if (imgMatch) {
        logoUrl = imgMatch[1];
      }
    }

    if (!logoUrl) {
      return "";
    }

    // Convert relative URL to absolute URL
    const baseUrl = new URL(website);
    const absoluteLogoUrl = new URL(logoUrl, baseUrl.origin).href;

    return absoluteLogoUrl;
  } catch (error) {
    console.error(`Failed to fetch logo from website ${website}:`, error);
    return "";
  }
}

export function getPublicBaseUrl(): string {
  const appUrl = process.env.NEXT_PUBLIC_BASE_URL;

  if (!appUrl) {
    throw new EnvConfigurationError(`NEXT_PUBLIC_BASE_URL is not set in ${process.env.NODE_ENV} environment. Value found: ${appUrl}`);
  }

  return appUrl;
}
